import type { TriageSuggestion, TriageSource } from '@/lib/drift-decision-copy'

export interface ConfigurationDrift {
  id: string
  tenantId: string
  component: string
  componentName?: string  // The actual resource name (e.g., "Test-App-666")
  setting: string
  oldValue: string
  newValue: string
  severity: DriftSeverity
  status: DriftStatus
  detectedAt: string
  updatedAt?: string  // When drift values evolved (updated vs original detection)
  // Customer-facing detection cadence for this resource type. Backend-resolved string
  // from one of: continuous-6h | daily-snapshot | weekly-snapshot | on-demand | daily-dsc.
  // Use formatCadenceLabel() to render — daily-dsc collapses to "nightly" in the UI so
  // customers don't see the pipeline name.
  cadence?: string
  // Best-effort "last time Guardian saw this drift instance" timestamp. Falls back to
  // detectedAt on legacy rows.
  lastCheckedAt?: string
  reviewedAt?: string
  reviewedBy?: string
  approvedAt?: string
  approvedBy?: string
  processedAt?: string  // When the drift was processed to ADO baseline (null = not yet processed)
  humanReadableDescription?: string
  businessImpact?: string
  recommendedAction?: string
  riskScore?: number
  complianceFrameworkImpact: ComplianceFrameworkImpact[]
  latestAuditEntry?: {
    eventType: string
    triggeredBy: string
    eventTimestamp: string
  }
  // Wave 7: source discriminator. 'DSC' or 'TCM'. Populated by the C# side
  // for projected TCM rows (see ComplianceService.ProjectTcmDriftToConfigurationDrift).
  // Undefined on DSC-only callers; defaults to 'DSC' when consumed.
  sourceCollector?: string
  // Drift Triage Copilot: Guardian's advisory read of this row. All five are
  // written only by the triage pass and never dispose the row — the human
  // approves. Absent/null on un-triaged rows (or a build before the columns
  // shipped). See `triageSuggestionCopy` in `@/lib/drift-decision-copy`.
  triageSuggestion?: TriageSuggestion | null
  triageConfidence?: number | null
  triageReason?: string | null
  triageSource?: TriageSource | null
  triagedAt?: string | null
}

export enum DriftSeverity {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Critical = 'Critical'
}

export enum DriftStatus {
  New = 'New',
  Pending = 'Pending',
  PendingApproval = 'PendingApproval',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Remediated = 'Remediated',
  Ignored = 'Ignored',
  AwaitingRemediation = 'AwaitingRemediation'
}

export interface ComplianceFrameworkImpact {
  frameworkName: string
  controlId: string
  controlName: string
  impactDescription: string
  previousScore: number
  newScore: number
}

export interface ComplianceScore {
  percentage: number
  level: 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Critical'
  totalChecks: number
  passedChecks: number
  failedChecks: number
  lastUpdated: string
  /**
   * True when the baseline behind this score is under-captured (drifted
   * resources exceed monitored resources, or none are monitored). The
   * percentage is not trustworthy — surface a "baseline incomplete" treatment
   * rather than the (fabricated) 0%. Set by the backend ComplianceService.
   */
  baselineIncomplete?: boolean
}

export interface DashboardStats {
  totalTenants: number
  activeDrifts: number
  /**
   * True only when the payload carried a numeric `ActiveAlerts`; false in the
   * error fallback. Composers take `activeDriftsKnown ? activeDrifts :
   * undefined` so a failed call never renders as "no drift".
   */
  activeDriftsKnown?: boolean
  pendingProcessing: number
  criticalIssues: number
  issuesBySeverity: { critical: number; high: number; medium: number; low: number }
  complianceScore: ComplianceScore
  recentActivity: ActivityItem[]
  driftTrends: TrendData[]
  topRisks: RiskItem[]
  /**
   * Severity-ranked critical/high drift detail (server caps at 10). On
   * Hybrid tenants this is the DSC + TCM union produced by
   * `ComplianceService.GetCriticalDriftAsync` — the canonical source for the
   * Baseline Compliance "Top items worth your eye" section so it stays
   * consistent with `issuesBySeverity` (both come from the same dashboard
   * call). TCM-sourced rows carry `sourceCollector === 'TCM'` so the drift
   * detail drawer routes to the TCM audit endpoints. DSC-only tenants get the
   * DSC critical/high rows verbatim. Mapped from `ComplianceDashboard.CriticalDrift`
   * in `getDashboardStats`.
   */
  criticalDrift: ConfigurationDrift[]
}

export interface ActivityItem {
  id: string
  type:
    | 'drift_detected'
    | 'drift_resolved'
    | 'drift_approved_by_user'
    | 'drift_rejected_by_user'
    | 'drift_remediated'
    | 'tenant_added'
    | 'compliance_scan'
    | 'compliance_scan_failed'
    | 'compliance_scan_partial'
    | 'baseline_refresh'
    | 'baseline_refresh_failed'
    | 'baseline_refresh_partial'
    | 'scuba_scan'
    | 'scuba_scan_failed'
    | 'scuba_scan_partial'
    // Best Practice Analyzer (OPA multi-framework compliance evaluation).
    | 'bpa_scan'
    | 'bpa_scan_failed'
    | 'bpa_scan_partial'
    // App Posture (OAuth app + consent sync).
    | 'app_posture_scan'
    | 'app_posture_scan_failed'
    | 'app_posture_scan_partial'
  title: string
  description: string
  timestamp: string
  severity?: DriftSeverity
  tenantName?: string
  driftId?: string
  actionBy?: string
}

export interface TrendData {
  date: string
  critical: number
  high: number
  medium: number
  low: number
  info: number
}

// Week-over-week trend types for dashboard metrics
export interface MetricTrendResult {
  metricType: string
  currentValue: number
  previousWeekValue: number | null
  changeAmount: number | null
  changePercentage: number | null
  direction: 'up' | 'down' | 'flat'
  history: MetricDataPoint[]
}

export interface MetricDataPoint {
  date: string
  value: number
  context?: string
}

export interface DashboardTrends {
  secureScore: MetricTrendResult
  mfaAdoption: MetricTrendResult
  baselineCompliance: MetricTrendResult
  generatedAt: string
}

export interface RiskItem {
  component: string
  riskScore: number
  affectedTenants: number
  description: string
}

export interface Tenant {
  tenantId: string
  name: string
  domain: string
  isActive: boolean
  lastScanAt?: string
  createdAt?: string
  // Wave 7 (TCM migration): controls which ManagementTab renders on
  // /configuration-drift. 'DSC' (default — legacy single-source view),
  // 'Hybrid' (TCM + DSC for SPO/ODB), or 'TCM' (reserved). Set by
  // POST /api/tcm/admin/tenants/{tenantId}/promote-to-hybrid.
  baselineSource?: string
  // Tenant lifecycle origin: 'SelfService' (signup flow), 'Manual', or
  // 'Migrated'. Drives the first-run /setup wizard eligibility
  // (lib/onboarding-setup.ts) — a SelfService tenant that hasn't reached
  // 'Hybrid' still has setup steps to finish.
  onboardingStatus?: string
}

export interface User {
  id: string
  email: string
  displayName: string
  roles: string[]
  tenantAccess: string[]
}

export interface ApiResponse<T> {
  data: T
  success: boolean
  message?: string
  errors?: string[]
}

// SCuBA compliance types
export * from './scuba'

// User Insights types
export const DEFAULT_INACTIVITY_THRESHOLD_DAYS = 90

export interface UserInsightsSummary {
  tenantId: string
  totalUsers: number
  enabledUsers: number
  disabledUsers: number
  memberUsers: number
  guestUsers: number
  totalLicensedUsers: number
  disabledUsersWithLicenses: number
  inactiveUsersWithLicenses: number
  totalLicenseWaste: number
  licenseSkus: LicenseSkuSummary[]
  totalLicensesAvailable: number
  totalLicensesUsed: number
  totalLicensesUnassigned: number
  mfaEnabledUsers: number
  mfaDisabledUsers: number
  authenticatorAppUsers: number
  mfaAdoptionPercentage: number
  authenticatorAdoptionPercentage: number
  // Member-only MFA stats (excludes guests)
  mfaEnabledMembers: number
  enabledMemberUsers: number
  mfaMemberAdoptionPercentage: number
  authenticatorAppMembers: number
  authenticatorMemberAdoptionPercentage: number
  lastRefreshedAt: string
  lastRefreshedBy?: string
  refreshStatus: string
  nextScheduledRefresh?: string
  lastErrorMessage?: string
  inactivityThresholdDays?: number
  hasData: boolean
  /**
   * Whether Exchange Online mailbox classification ran to completion on the scan
   * behind this summary. When false while `hasData` is true, the shared/resource-
   * mailbox exclusions behind the No-MFA / Needs-attention counts and phish-
   * resistant aggregates are unreliable, so those surfaces show a transient
   * "refining" state instead of silently inflated numbers. The headline MFA
   * adoption % is unaffected (it excludes nothing). Absent on summaries produced
   * before this shipped, so treat `undefined` as "available" (do not gate).
   */
  mailboxClassificationAvailable?: boolean
  // Phish-resistant MFA stats
  phishResistantMfaUsers: number
  phishResistantMfaPercentage: number
  fido2Users: number
  windowsHelloUsers: number
  certificateAuthUsers: number
  // Directory filter counts
  activeMemberUsers: number
  activeGuestUsers: number
  inactiveMemberUsers: number
  inactiveGuestUsers: number
  needsAttentionUsers: number
  noMfaEnabledUsers: number
  /**
   * Enabled member Global Administrators without MFA. Drives the
   * Operations rail's GA-no-MFA row and the Identity priority-actions
   * "Enforce MFA on N GA accounts" template. Tenants whose last scan
   * predates GA collection report 0 (graceful degrade rather than null).
   */
  noMfaGlobalAdmins: number
  /**
   * Enabled member Global Administrators flagged inactive per the
   * per-tenant inactivity threshold. Drives the dormant-GA row.
   * Pre-flag tenants report 0.
   */
  inactiveGlobalAdmins: number
  // Resource account tracking
  resourceAccountCount: number
  totalEstimatedMonthlyWasteUsd?: number
  totalEstimatedAnnualWasteUsd?: number
  topIdleSkus?: TopIdleSkuSummary[]
}

export interface LicenseSkuSummary {
  skuId: string
  skuPartNumber: string
  displayName: string
  totalLicenses: number
  usedLicenses: number
  availableLicenses: number
  utilizationPercentage: number
  monthlyPricePerLicense?: number
  estimatedMonthlyWasteUsd?: number
}

export interface TopIdleSkuSummary {
  skuPartNumber: string
  displayName: string
  idleSeats: number
  estimatedMonthlyWasteUsd: number
}

export interface UserDetailItem {
  userId: string
  userPrincipalName: string
  displayName: string
  jobTitle?: string
  userType: string
  accountEnabled: boolean
  licenseCount: number
  assignedLicenses: string[]
  lastSignInDateTime?: string
  isInactive: boolean
  hasMfaEnabled: boolean
  hasAuthenticatorApp: boolean
  isLicenseWaste: boolean
  isSharedMailbox: boolean
  isResourceAccount: boolean
  resourceAccountType?: string
  accountCreatedDate: string | null
  issueType: string
  // Phish-resistant MFA details
  hasPhishResistantMfa: boolean
  hasFido2Key: boolean
  hasWindowsHello: boolean
  hasCertificateAuth: boolean
  /**
   * Whether the user is a Global Administrator. Derived at scan time from
   * Microsoft Graph directory-role membership; false for users from tenants
   * whose last scan ran before role collection shipped.
   */
  isGlobalAdmin?: boolean
  /**
   * Directory role display names assigned to the user (e.g. "Global
   * Administrator", "User Administrator"). Empty for users from tenants
   * scanned before role collection shipped.
   */
  roles?: string[]
}

export interface UserDetailListResult {
  items: UserDetailItem[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export type UserIssueFilter =
  | 'All'
  | 'DisabledWithLicenses'
  | 'InactiveWithLicenses'
  | 'NoMfa'
  | 'NoAuthenticatorApp'
  | 'LicenseWaste'
  | 'InactiveMembers'
  | 'InactiveGuests'
  | 'NoPhishResistantMfa'
  | 'HasPhishResistantMfa'
  | 'ActiveMembers'
  | 'ActiveGuests'
  | 'EnabledInactiveMembers'
  | 'NeedsAttention'
  | 'ResourceAccounts'

export interface PhishResistantMfaSummary {
  totalEnabledMembers: number
  phishResistantMfaUsers: number
  phishResistantMfaPercentage: number
  fido2Users: number
  windowsHelloUsers: number
  certificateAuthUsers: number
  standardMfaOnlyUsers: number
  noMfaUsers: number
}

export interface UserInsightsTrendsResult {
  history: UserInsightsHistoryPoint[]
  trendSummary: UserInsightsTrendSummary
}

export interface UserInsightsHistoryPoint {
  date: string
  totalUsers: number
  enabledUsers: number
  mfaMemberAdoptionPercentage: number
  phishResistantMfaPercentage: number
  totalLicenseWaste: number
  inactiveUsers: number
  phishResistantMfaUsers: number
  fido2Users: number
  windowsHelloUsers: number
}

export interface UserInsightsTrendSummary {
  mfaAdoption: MetricTrendResult
  phishResistantMfa: MetricTrendResult
  licenseWaste: MetricTrendResult
  totalUsers: MetricTrendResult
}

export interface RefreshStatusInfo {
  status: string
  lastRefreshedAt?: string
  lastRefreshedBy?: string
  nextScheduledRefresh?: string
  currentJobId?: string
  progressPercentage?: number
  currentStep?: string
  errorMessage?: string
}

export interface RefreshResponse {
  jobId: string
  status: string
  message: string
}

// Microsoft Secure Score types
export * from './secureScore'

// Standards Compliance types (OPA-based multi-framework evaluation)
export * from './standardsCompliance'

// Executive Report types
export * from './executiveReport'

// QA Policy Validation types
export * from './qaValidation'
